import { AnimatePresence, motion } from 'framer-motion';
import {
  CalendarRange,
  Camera,
  ChevronDown,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  NumPad,
  ReceiptScannerSheet,
} from '../../components';
import { useAppData } from '../../data';
import { cn } from '../../lib/cn';
import { formatRelativeDate, toIsoDate } from '../../lib/date';
import { suggestCategoryFromMerchant } from '../../lib/merchantCategories';
import {
  suggestMerchants,
  type MerchantMemoryEntry,
} from '../../lib/merchantMemory';
import { supportsReceiptCapture } from '../../lib/platform';
import { springGentle, springQuick, tapScale } from '../../tokens';

export type EntryType = 'expense' | 'income';

export type EntryDraft = {
  type: EntryType;
  description: string;
  categoryId: string;
  date: string; // ISO yyyy-mm-dd
  amount: number;
  /** Days to amortize over. 1 = no spread. */
  spreadDays?: number;
};

type TransactionFormProps = {
  type: EntryType;
  /** Pre-fill description, categoryId, date, amount, spreadDays when editing. */
  initial?: Partial<Omit<EntryDraft, 'type'>>;
  /** NumPad's Done key label — "Save" for add, "Update" for edit. */
  doneLabel?: string;
  onSave: (draft: EntryDraft) => void;
  /** Fires whenever the user has typed something the parent should
   *  guard against discarding (description text or non-zero amount). */
  onDirtyChange?: (dirty: boolean) => void;
};

/** Preset spread windows. "Today" means no spread. */
const SPREAD_PRESETS: Array<{ label: string; days: number }> = [
  { label: 'Today', days: 1 },
  { label: '7d', days: 7 },
  { label: '30d', days: 30 },
  { label: '60d', days: 60 },
  { label: '90d', days: 90 },
];

/**
 * Shared body for transaction add/edit flows. Renders the description
 * input + category dropdown + date input + spread picker + NumPad.
 * Wrap with whatever chrome is appropriate (slide-down panel for
 * Today, BottomSheet for Activity edit).
 *
 * Spread is opt-in for expenses only — spreading income makes no
 * accounting sense in our model. The picker is hidden for income.
 */
export function TransactionForm({
  type,
  initial,
  doneLabel = 'Save',
  onSave,
  onDirtyChange,
}: TransactionFormProps) {
  const data = useAppData();

  const categories = useMemo(
    () =>
      data.categories
        .filter((c) => c.type === type)
        .sort((a, b) => a.order - b.order),
    [data.categories, type],
  );

  const todayIso = toIsoDate(new Date());
  const [description, setDescription] = useState(initial?.description ?? '');
  const [categoryId, setCategoryId] = useState(
    initial?.categoryId ?? categories[0]?.id ?? '',
  );
  const [date, setDate] = useState(initial?.date ?? todayIso);
  const [amount, setAmount] = useState(initial?.amount ?? 0);
  const [spreadDays, setSpreadDays] = useState<number>(initial?.spreadDays ?? 1);
  // Live mirror of the NumPad buffer so the Apply/Today label can flip
  // as the user types — without committing the value to spreadDays
  // until they tap Apply.
  const [liveSpreadInput, setLiveSpreadInput] = useState<number>(
    initial?.spreadDays && initial.spreadDays > 1 ? initial.spreadDays : 0,
  );
  // Track whether the user picked a non-preset value so the Custom NumPad
  // stays visible while they're typing.
  const [customOpen, setCustomOpen] = useState<boolean>(() => {
    const isPreset = SPREAD_PRESETS.some(
      (p) => p.days === (initial?.spreadDays ?? 1),
    );
    return !isPreset;
  });

  // If categories load asynchronously, pin the dropdown to the first option.
  useEffect(() => {
    if (!categoryId && categories[0]) {
      setCategoryId(categories[0].id);
    }
  }, [categoryId, categories]);

  // Expose a "form has user input" flag for parents that want to guard
  // against accidental discard. Dirty when either the description has
  // characters or amount is non-zero, ignoring values supplied via
  // `initial` (those are pre-fills, not user input).
  useEffect(() => {
    if (!onDirtyChange) return;
    const initialDescription = initial?.description ?? '';
    const initialAmount = initial?.amount ?? 0;
    const dirty =
      description.trim() !== initialDescription.trim() ||
      amount !== initialAmount;
    onDirtyChange(dirty);
  }, [description, amount, initial?.description, initial?.amount, onDirtyChange]);

  // Merchant memory + keyword categorizer suggestions. Debounced 100ms
  // so we don't run a localStorage read on every keystroke.
  const [merchantSuggestions, setMerchantSuggestions] = useState<
    MerchantMemoryEntry[]
  >([]);
  const [keywordHint, setKeywordHint] = useState<string | null>(null);
  // Bumped whenever we externally fill the amount via a chip tap, so
  // the NumPad re-mounts with the new initialValue (it owns its buffer).
  const [chipFillCount, setChipFillCount] = useState(0);

  useEffect(() => {
    const t = setTimeout(() => {
      const q = description.trim();
      if (q.length === 0) {
        setMerchantSuggestions([]);
        setKeywordHint(null);
        return;
      }
      const matches = suggestMerchants(q, 5);
      setMerchantSuggestions(matches);
      if (matches.length === 0 && q.length >= 3 && type === 'expense') {
        const id = suggestCategoryFromMerchant(q, categories);
        setKeywordHint(
          id ? categories.find((c) => c.id === id)?.name ?? null : null,
        );
      } else {
        setKeywordHint(null);
      }
    }, 100);
    return () => clearTimeout(t);
  }, [description, categories, type]);

  const [scannerOpen, setScannerOpen] = useState(false);
  /** True on every platform that can produce an image. The scanner
   *  sheet handles the OCR-vs-manual branch internally. */
  const captureAvailable = supportsReceiptCapture();

  // Spread row collapses into a single inline toggle by default.
  const [spreadExpanded, setSpreadExpanded] = useState(false);
  const spreadActive = spreadDays > 1;
  const spreadLabel = spreadActive
    ? `Spread over ${spreadDays} days`
    : 'Today only';

  // Hidden native date input — the visible pill triggers showPicker()
  // (or falls back to .click()) so we get the system date wheel/calendar.
  const dateInputRef = useRef<HTMLInputElement | null>(null);
  const openDatePicker = () => {
    const el = dateInputRef.current;
    if (!el) return;
    const anyEl = el as HTMLInputElement & { showPicker?: () => void };
    if (typeof anyEl.showPicker === 'function') anyEl.showPicker();
    else el.click();
  };

  const selectedCategory = categories.find((c) => c.id === categoryId);
  const selectedCategoryColor = selectedCategory?.color;
  const selectedCategoryName = selectedCategory?.name ?? 'Choose category';

  const triggerScan = () => {
    // If the user has already typed something, ask before opening —
    // the scanner creates its own transaction and would discard the
    // current draft.
    const hasDraft =
      description.trim().length > 0 || amount > 0;
    if (hasDraft) {
      const ok =
        typeof window !== 'undefined'
          ? window.confirm(
              'Scanning a receipt creates a new transaction. Replace your current entry?',
            )
          : true;
      if (!ok) return;
    }
    setScannerOpen(true);
  };

  const applyChip = (entry: MerchantMemoryEntry) => {
    setDescription(entry.merchant);
    if (entry.topCategoryId) {
      const cat = categories.find((c) => c.id === entry.topCategoryId);
      if (cat) setCategoryId(cat.id);
    }
    if (amount === 0 && entry.lastAmount > 0) {
      setAmount(entry.lastAmount);
      setChipFillCount((c) => c + 1);
    }
  };

  const submit = (committedAmount: number) => {
    if (!categoryId) return;
    onSave({
      type,
      description: description.trim(),
      categoryId,
      date,
      amount: committedAmount,
      spreadDays: spreadDays > 1 ? spreadDays : undefined,
    });
  };

  const isCustomActive = !SPREAD_PRESETS.some((p) => p.days === spreadDays);
  // Spread works for both directions:
  //   - Expenses: amortize a $500 couch over 45 days at $11.11/day
  //   - Income:   spread a $5,000 freelance check over 30 days so the
  //               daily budget steadies instead of spiking
  const showSpreadRow = true;

  return (
    <>
      <div className="px-card-pad pt-3 pb-1 flex flex-col gap-3">
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={
              type === 'expense'
                ? 'What was this for?'
                : 'Where did this come from?'
            }
            autoCapitalize="sentences"
            maxLength={80}
            className="flex-1 h-11 px-4 rounded-button bg-surface-secondary text-[15px] text-text-primary placeholder:text-text-tertiary focus:outline-none focus:ring-2 focus:ring-accent/40"
          />
          {captureAvailable && type === 'expense' && doneLabel !== 'Update' ? (
            <motion.button
              type="button"
              onClick={triggerScan}
              whileTap={tapScale}
              transition={springQuick}
              aria-label="Attach receipt"
              title="Attach receipt"
              className="shrink-0 h-11 w-11 inline-flex items-center justify-center rounded-button bg-surface-secondary text-text-secondary"
            >
              <Camera size={18} strokeWidth={1.75} />
            </motion.button>
          ) : null}
        </div>
        <div className="flex flex-col gap-2">
          {/* Category — labeled two-line pill. Native <select> is
              absolutely overlaid at opacity 0 so taps open the system
              picker; the visible content above is purely presentational. */}
          <div className="relative px-4 py-2.5 rounded-button bg-surface-secondary">
            <div className="text-[11px] font-medium uppercase tracking-[0.06em] text-text-tertiary">
              Category
            </div>
            <div className="mt-0.5 flex items-center justify-between gap-2">
              <span className="inline-flex items-center gap-2 min-w-0">
                <span
                  className="w-2 h-2 rounded-full opacity-75 shrink-0"
                  style={{
                    backgroundColor: selectedCategoryColor || '#AEAEB2',
                  }}
                />
                <span className="text-[15px] font-medium text-text-primary truncate">
                  {selectedCategoryName}
                </span>
              </span>
              <ChevronDown
                size={14}
                strokeWidth={1.75}
                className="text-text-tertiary shrink-0"
              />
            </div>
            <select
              value={categoryId}
              onChange={(e) => setCategoryId(e.target.value)}
              aria-label="Category"
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer appearance-none"
            >
              {categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>

          {/* Date — labeled two-line pill with overlaid native input. */}
          <div className="relative px-4 py-2.5 rounded-button bg-surface-secondary">
            <div className="text-[11px] font-medium uppercase tracking-[0.06em] text-text-tertiary">
              Date
            </div>
            <div className="mt-0.5 flex items-center justify-between gap-2">
              <span className="text-[15px] font-medium text-text-primary truncate">
                {formatRelativeDate(date)}
              </span>
              <ChevronDown
                size={14}
                strokeWidth={1.75}
                className="text-text-tertiary shrink-0"
              />
            </div>
            <button
              type="button"
              onClick={openDatePicker}
              aria-label="Choose date"
              className="absolute inset-0 w-full h-full"
            />
            <input
              ref={dateInputRef}
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              aria-hidden
              tabIndex={-1}
              className="sr-only"
            />
          </div>

          {/* Spread over time — full-width labeled pill teaches the
              feature even before it's used. Tap expands the chip
              picker; the body label updates to reflect the choice. */}
          {showSpreadRow ? (
            <button
              type="button"
              onClick={() => setSpreadExpanded((v) => !v)}
              aria-expanded={spreadExpanded}
              className={cn(
                'w-full px-4 py-2.5 rounded-button bg-surface-secondary',
                'text-left inline-flex items-center justify-between gap-2',
              )}
            >
              <span className="flex flex-col min-w-0">
                <span className="text-[11px] font-medium uppercase tracking-[0.06em] text-text-tertiary">
                  Spread over time
                </span>
                <span
                  className={cn(
                    'text-[15px] font-medium truncate',
                    spreadActive ? 'text-accent' : 'text-text-primary',
                  )}
                >
                  {spreadLabel}
                </span>
              </span>
              <span className="inline-flex items-center gap-2 shrink-0">
                {spreadActive && amount > 0 ? (
                  <span className="text-[12px] font-mono tabular-nums text-text-secondary">
                    ≈ {(amount / spreadDays).toFixed(2)}/day
                  </span>
                ) : null}
                <CalendarRange
                  size={14}
                  strokeWidth={1.8}
                  className="text-text-tertiary"
                />
                <motion.span
                  animate={{ rotate: spreadExpanded ? 180 : 0 }}
                  transition={springQuick}
                  className="inline-flex"
                >
                  <ChevronDown
                    size={14}
                    strokeWidth={1.75}
                    className="text-text-tertiary"
                  />
                </motion.span>
              </span>
            </button>
          ) : null}
        </div>

        {showSpreadRow ? (
          <div className="flex flex-col">
            <AnimatePresence initial={false}>
              {spreadExpanded ? (
                <motion.div
                  key="spread-body"
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ type: 'spring', stiffness: 220, damping: 26 }}
                  style={{ overflow: 'hidden' }}
                >
                  <div className="flex flex-wrap gap-2 pt-1">
                    {SPREAD_PRESETS.map((p) => {
                      const active = !customOpen && spreadDays === p.days;
                      return (
                        <SpreadChip
                          key={p.label}
                          label={p.label}
                          active={active}
                          onClick={() => {
                            setSpreadDays(p.days);
                            setCustomOpen(false);
                          }}
                        />
                      );
                    })}
                    <SpreadChip
                      label={
                        customOpen || isCustomActive
                          ? `Custom: ${spreadDays}d`
                          : 'Custom'
                      }
                      active={customOpen || isCustomActive}
                      onClick={() => setCustomOpen((v) => !v)}
                    />
                  </div>
                </motion.div>
              ) : null}
            </AnimatePresence>

            <AnimatePresence initial={false}>
              {spreadExpanded && customOpen ? (
                <motion.div
                  key="custom"
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={springGentle}
                  style={{ overflow: 'hidden' }}
                >
                  <div className="rounded-button bg-surface-secondary mt-1">
                    <NumPad
                      key={`spread-${initial?.spreadDays ?? 0}`}
                      // Start at 0 (empty display) for fresh entries so
                      // the user can type the digits they want without
                      // first clearing a default. For edits, restore the
                      // existing spread length.
                      initialValue={spreadDays > 1 ? spreadDays : 0}
                      onChange={(v) => setLiveSpreadInput(Math.round(v))}
                      onDone={(v) => {
                        // Clamp at apply time only — while typing the
                        // user can backspace freely down to 0/1.
                        // 0 or 1 means no spread (single-day transaction).
                        const r = Math.round(v);
                        setSpreadDays(r > 1 ? r : 1);
                        setCustomOpen(false);
                      }}
                      doneLabel={liveSpreadInput > 1 ? 'Apply' : 'Today'}
                      caption="Days to spread over"
                      // 0/1 here means "no spread" — a valid Today choice.
                      allowZero
                      formatValue={(n) => {
                        const r = Math.round(n);
                        return r <= 1 ? 'Today (no spread)' : `${r} days`;
                      }}
                    />
                  </div>
                </motion.div>
              ) : null}
            </AnimatePresence>
          </div>
        ) : null}
      </div>

      {merchantSuggestions.length > 0 ? (
        <div className="px-card-pad pb-2">
          <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1 scrollbar-none">
            {merchantSuggestions.map((m) => (
              <motion.button
                key={m.lower}
                type="button"
                onClick={() => applyChip(m)}
                whileTap={tapScale}
                transition={springQuick}
                className="shrink-0 inline-flex items-center gap-1.5 rounded-pill bg-surface-secondary px-3 h-8 text-[12px] font-medium text-text-primary"
              >
                <span className="truncate max-w-[140px]">
                  {m.merchant.length > 18
                    ? `${m.merchant.slice(0, 18)}…`
                    : m.merchant}
                </span>
                {m.lastAmount > 0 ? (
                  <span className="font-mono tabular-nums text-text-tertiary">
                    ${m.lastAmount.toFixed(2)}
                  </span>
                ) : null}
              </motion.button>
            ))}
          </div>
        </div>
      ) : keywordHint ? (
        <div className="px-card-pad pb-2 -mt-1">
          <span className="text-[12px] text-text-tertiary">
            Suggested:{' '}
            <span className="text-text-secondary font-medium">
              {keywordHint}
            </span>
          </span>
        </div>
      ) : null}

      <NumPad
        key={`np-${chipFillCount}`}
        initialValue={amount}
        onChange={setAmount}
        onDone={submit}
        doneLabel={doneLabel}
        caption={null}
      />
      {captureAvailable ? (
        <ReceiptScannerSheet
          open={scannerOpen}
          onClose={() => setScannerOpen(false)}
        />
      ) : null}
    </>
  );
}

function SpreadChip({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <motion.button
      type="button"
      onClick={onClick}
      whileTap={tapScale}
      transition={springQuick}
      aria-pressed={active}
      className={cn(
        'inline-flex items-center justify-center rounded-pill px-3 h-8',
        'text-[12px] font-medium',
        active
          ? 'bg-accent text-white'
          : 'bg-surface-secondary text-text-secondary',
      )}
    >
      {label}
    </motion.button>
  );
}
